import type { intlFormatPropTypes } from "react-intl";

import type { OwnerListing } from "../../services/listings.service";
import {
  ACTIVATED_STATUS,
  INSTANT_BOOK_GROUP_MAX_LISTINGS,
  INSTANT_BOOK_THUMBNAIL_PX,
} from "../constants/host-hub-instant-book.consts";
import type { HostHubInstantBookListing } from "../types/model/instant-book-listing";

import { pickListingThumbnailUrl } from "./cloudinary-image.utils";
import { formatListingRowTitle } from "./host-hub-listing.utils";

const toInstantBookListing = (
  listing: OwnerListing,
): HostHubInstantBookListing => ({
  id: listing._id,
  mediaAlt: listing.title,
  mediaUrl: pickListingThumbnailUrl({
    images: listing.images,
    size: INSTANT_BOOK_THUMBNAIL_PX,
  }),
  primaryCtaRoute: `/mylistings/${listing._id}/instant-book`,
  spaceTitle: listing.title,
  useType: listing.use_type,
});

/**
 * Picks the listings the Instant Book nudge should surface, capped at
 * `INSTANT_BOOK_GROUP_MAX_LISTINGS`:
 * - Eligibility (`is_instant_book_eligible_host`) is denormalized onto every
 *   listing with the same value, so reading the first listing is sufficient.
 *   If the host isn't eligible they can't enable IB at all, so the nudge is
 *   suppressed entirely (returns `[]`).
 * - Otherwise we surface the activated listings that still have IB disabled
 *   (`is_instant_book_enabled` is false); when all listings are enabled there
 *   is nothing to nudge. Non-activated listings (e.g. pending, deactivated) are
 *   excluded since the shared owner-listings query is no longer filtered to
 *   activated server-side.
 */
export const toInstantBookListings = (
  listings: OwnerListing[] | undefined,
): HostHubInstantBookListing[] => {
  const ownerListings = listings ?? [];

  if (!ownerListings[0]?.is_instant_book_eligible_host) return [];

  return ownerListings
    .filter((listing) => listing.status === ACTIVATED_STATUS)
    .filter((listing) => !listing.is_instant_book_enabled)
    .slice(0, INSTANT_BOOK_GROUP_MAX_LISTINGS)
    .map(toInstantBookListing);
};

/**
 * Resolves a listing row label as `"[Space Name] - [Listing Type]"` for an
 * Instant Book listing (see `formatListingRowTitle`).
 */
export const formatInstantBookListingTitle = ({
  intl,
  listing,
}: {
  intl: intlFormatPropTypes;
  listing: HostHubInstantBookListing;
}): string =>
  formatListingRowTitle({
    intl,
    spaceTitle: listing.spaceTitle,
    useType: listing.useType,
  });
